import { useState, useEffect } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { MaskReveal, FadeUp, EASE } from "./Reveal";
import { LineField, FillButton } from "./Interactive";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const apply = () => {
      if (window.__gaiaContactPrefill) {
        setForm((f) => ({ ...f, message: window.__gaiaContactPrefill }));
      }
    };
    apply();
    window.addEventListener("gaia:prefill-contact", apply);
    return () => window.removeEventListener("gaia:prefill-contact", apply);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        website: form.website,
      });
      setSent(true);
    } catch (err) {
      setError("Something went quiet on our end. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-gaia-sand py-20 md:py-28" data-testid="contact-section">
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-12 px-6 md:grid-cols-12 md:gap-16 md:px-12">
        <div className="md:col-span-5">
          <FadeUp>
            <span className="font-sans text-[11px] uppercase tracking-[0.32em] text-gaia-olive">Get in Touch</span>
          </FadeUp>
          <MaskReveal
            as="h2"
            lines={["Let's Begin"]}
            className="mt-5 font-serif text-6xl font-light tracking-tight text-gaia-charcoal md:text-7xl"
            data-testid="contact-headline"
          />
          <FadeUp delay={0.15}>
            <p className="mt-7 max-w-sm font-sans text-base font-light leading-loose text-gaia-charcoal/70">
              Whether you're curious about a future gathering, collaboration, or simply want to say hello, I'd love to hear from you.
            </p>
            <a
              href="mailto:hello@gaiaspace.life"
              className="group relative mt-7 inline-block font-serif text-2xl font-light italic text-gaia-charcoal transition-colors duration-500 hover:text-gaia-terracotta"
              data-testid="contact-email-link"
            >
              hello@gaiaspace.life
              <span className="absolute -bottom-0.5 left-0 h-px w-full origin-left scale-x-0 bg-gaia-terracotta transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100" />
            </a>
          </FadeUp>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          <AnimatePresence mode="wait">
            {sent ? (
              <motion.div
                key="thanks"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASE }}
                className="flex h-full min-h-[240px] items-center"
                data-testid="contact-success"
              >
                <p className="font-serif text-3xl font-light italic leading-relaxed text-gaia-charcoal md:text-4xl">
                  Thank you, {form.name.split(" ")[0]}. Your words have arrived — I'll be in touch soon.
                </p>
              </motion.div>
            ) : (
              <motion.form key="form" exit={{ opacity: 0 }} onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <LineField label="Name" value={form.name} onChange={set("name")} required testId="contact-name-input" />
                <LineField label="Email" type="email" value={form.email} onChange={set("email")} required testId="contact-email-input" />
                <LineField label="Message" textarea value={form.message} onChange={set("message")} required testId="contact-message-input" />
                <div aria-hidden="true" className="sr-only">
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={set("website")}
                    data-testid="contact-honeypot-input"
                  />
                </div>
                {error && <p className="font-sans text-sm font-light text-gaia-terracotta" data-testid="contact-error">{error}</p>}
                <div className="pt-2">
                  <FillButton type="submit" disabled={loading} data-testid="contact-submit-btn">
                    {loading ? "Sending…" : "Send"}
                  </FillButton>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Contact;
